import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "../../Header";
import Sidebar from "../../Sidebar";
import IndividualReportHistory from "./IndividualReportHistory";
import ConsolidatedReportHistory from "./ConsolidatedReportHistory";
import "../../../styles/ReportHistory.css";
import "../../../styles/theme.css";
import { useSidebar } from "../../../App";
import ReportModal from "../ReportModal";

export default function ReportHistory() {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_page" });
  const { sidebarCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [selectedReport, setSelectedReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [activeTab, setActiveTab] = useState("individual");
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [consolidatedResetNonce, setConsolidatedResetNonce] = useState(0);
  const [individualLoading, setIndividualLoading] = useState(true);
  const [consolidatedLoading, setConsolidatedLoading] = useState(true);

  const handleReportLoaded = useCallback((report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  }, []);

  const handleActivateIndividual = useCallback(() => {
    setActiveTab("individual");
  }, []);

  const handleConsolidated = useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  const refreshing = individualLoading || consolidatedLoading;

  return (
    <div className="theme-whbl">
      <Header />
      <div className="main-container">
        <div className={`${sidebarCollapsed ? "col-1" : "col-2"} p-0`}>
          <Sidebar />
        </div>

        <main
          className={`${sidebarCollapsed ? "col-11" : "col-10"}`}
          style={{ width: "100%", overflowX: "auto" }}
        >
          <div className="dashboard">
            <div className="pm-header">
              <div className="pm-title-section">
                <h1 className="pm-title">{t("reports")}</h1>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <button
                  onClick={() => setRefreshNonce((n) => n + 1)}
                  className="btn btn-secondary"
                  disabled={refreshing}
                >
                  {refreshing ? t("refreshing") : t("refresh")}
                </button>
              </div>
            </div>

            <div className="rh-tabs">
              <button
                type="button"
                className={`rh-tab ${activeTab === "individual" ? "active" : ""}`}
                onClick={() => setActiveTab("individual")}
              >
                {t("individual")}
              </button>
              <button
                type="button"
                className={`rh-tab ${activeTab === "consolidated" ? "active" : ""}`}
                onClick={() => setActiveTab("consolidated")}
              >
                {t("consolidated")}
              </button>
            </div>

            <div style={{ display: activeTab === "individual" ? "block" : "none" }}>
              <IndividualReportHistory
                refreshNonce={refreshNonce}
                onLoadingChange={setIndividualLoading}
                onDetailLoadingChange={setLoadingReport}
                onReportLoaded={handleReportLoaded}
                onActivate={handleActivateIndividual}
                onConsolidated={handleConsolidated}
              />
            </div>

            <div style={{ display: activeTab === "consolidated" ? "block" : "none" }}>
              <ConsolidatedReportHistory
                refreshNonce={refreshNonce}
                onLoadingChange={setConsolidatedLoading}
                onDetailLoadingChange={setLoadingReport}
                onReportLoaded={handleReportLoaded}
                detailLoading={loadingReport}
                resetFilterNonce={consolidatedResetNonce}
              />
            </div>
          </div>
        </main>
      </div>

      {loadingReport && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }}>
          <div style={{
            textAlign: "center",
            color: "white"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              border: "6px solid rgba(255, 255, 255, 0.3)",
              borderTop: "6px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px"
            }}></div>
            <div style={{ fontSize: "18px", fontWeight: "500" }}>
              {t("loadingReportDetails")}
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      <ReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reportData={selectedReport}
        navigate={navigate}
      />
    </div>
  );
}
